import { useTranslation } from "react-i18next"

import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/cn"

export type ProgressTone = "neutral" | "success" | "warning" | "danger"

export interface ProgressBarThresholds {
  /** Percentage of `max` at or above which the tone becomes "warning". */
  warning: number
  /** Percentage of `max` at or above which the tone becomes "danger". */
  danger: number
}

const DEFAULT_THRESHOLDS: ProgressBarThresholds = { warning: 75, danger: 90 }

const TRACK_CLASS: Record<ProgressTone, string> = {
  neutral: "bg-muted",
  success: "bg-success-subtle",
  warning: "bg-warning-subtle",
  danger: "bg-destructive-subtle",
}

const INDICATOR_CLASS: Record<ProgressTone, string> = {
  neutral: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
}

export interface ProgressBarProps {
  /**
   * Percentage reached, out of `max`. `null` renders the "Unlimited"
   * variant -- a full, static bar with no numeric claim (an unmetered
   * wallet or plan): no `aria-valuenow`, and the state is announced once,
   * through `aria-valuetext` alone (not a separate sr-only node too).
   */
  value: number | null
  max?: number
  /** Accessible name; this is the only thing that tells a screen reader
   * what the bar measures. */
  label: string
  /** Overrides the threshold-derived tone. */
  tone?: ProgressTone
  thresholds?: ProgressBarThresholds
  className?: string
}

/**
 * `role="progressbar"` with `aria-valuenow`/`aria-valuemin`/`aria-valuemax`
 * comes from Radix's `Progress` primitive for free (it sets all three
 * whenever `value` is a number); this wraps it with tokens-only tone
 * colouring by threshold and the "Unlimited" variant (LLD §8.1).
 */
export function ProgressBar({
  value,
  max = 100,
  label,
  tone,
  thresholds = DEFAULT_THRESHOLDS,
  className,
}: ProgressBarProps) {
  const { t } = useTranslation("common")
  const pct = value === null ? null : max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0
  const resolvedTone: ProgressTone =
    tone ??
    (pct === null
      ? "neutral"
      : pct >= thresholds.danger
        ? "danger"
        : pct >= thresholds.warning
          ? "warning"
          : "success")

  return (
    <div className={cn("space-y-1", className)}>
      <Progress
        value={pct ?? 100}
        aria-label={label}
        className={TRACK_CLASS[resolvedTone]}
        indicatorClassName={INDICATOR_CLASS[resolvedTone]}
        {...(pct === null && { "aria-valuetext": t("progress.unlimited") })}
      />
    </div>
  )
}
